import { AgentExecutionError, ConfigurationError, LLMProviderError, UserInputError } from "./errors-CEDWDXew.js";
import { logIssue, logWarn } from "./logging-B7X3yL0T.js";
import { getAgentProvider } from "./provider-factory-Cs_2DBhe.js";
import { APICallError, NoObjectGeneratedError, NoOutputGeneratedError, Output, RetryError, generateText, stepCountIs } from "ai";

//#region src/internal/agents/callEmbeddedAgent.ts
/**
* Resolve the underlying provider failure from an AI SDK error.
*
* After retries, the SDK wraps retryable 5xx/network failures in RetryError.
* Prefer the last error, then walk earlier errors for the first APICallError.
*/
function resolveProviderApiCallError(error) {
	if (APICallError.isInstance(error)) return error;
	if (!RetryError.isInstance(error)) return null;
	if (APICallError.isInstance(error.lastError)) return error.lastError;
	for (let i = error.errors.length - 1; i >= 0; i--) {
		const candidate = error.errors[i];
		if (APICallError.isInstance(candidate)) return candidate;
	}
	return null;
}
function toLLMProviderError(error, originalError = error) {
	if (error.message.includes("Country, region, or territory not supported")) return new LLMProviderError("The AI provider (OpenAI) does not support requests from your region. This is a restriction imposed by OpenAI on certain countries and territories. Please contact support if you believe this is an error.", { cause: originalError });
	const statusCode = error.statusCode;
	if (statusCode && statusCode >= 400 && statusCode < 500) return new LLMProviderError(`The AI provider returned an error: ${error.message}. This may be a configuration, quota, or account issue with the upstream AI provider.`, { cause: originalError });
	if (!statusCode || statusCode >= 500) return new LLMProviderError(`The AI provider is currently unavailable${statusCode ? ` (HTTP ${statusCode})` : ""}: ${error.message}. Please try again later.`, { cause: originalError });
	return new LLMProviderError(`The AI provider returned an error: ${error.message}. This may be a configuration, quota, or account issue with the upstream AI provider.`, { cause: originalError });
}
/**
* Call an embedded agent with tool call capture
* This is the standard way to call embedded AI agents within MCP tools
*
* Error handling:
* - Errors are re-thrown for the calling agent to handle
* - Each agent can implement its own error handling strategy
*/
async function callEmbeddedAgent({ system, prompt, tools, schema }) {
	const capturedToolCalls = [];
	const provider = getAgentProvider();
	try {
		const result = await generateText({
			model: provider.getModel(),
			system,
			prompt,
			tools,
			stopWhen: stepCountIs(5),
			experimental_output: Output.object({ schema }),
			experimental_telemetry: {
				isEnabled: true,
				functionId: "callEmbeddedAgent"
			},
			providerOptions: provider.getProviderOptions(),
			onStepFinish: (event) => {
				if (event.toolCalls && event.toolCalls.length > 0) for (const toolCall of event.toolCalls) capturedToolCalls.push({
					toolName: toolCall.toolName,
					args: toolCall.input
				});
			}
		});
		if (!result.experimental_output) throw new NoOutputGeneratedError({ message: "No output generated." });
		const rawOutput = result.experimental_output;
		if (typeof rawOutput === "object" && rawOutput !== null && "error" in rawOutput && typeof rawOutput.error === "string") throw new UserInputError(rawOutput.error);
		const parsedResult = schema.safeParse(rawOutput);
		if (!parsedResult.success) throw new UserInputError(`Invalid agent response: ${parsedResult.error.message}`);
		return {
			result: parsedResult.data,
			toolCalls: capturedToolCalls
		};
	} catch (error) {
		if (NoObjectGeneratedError.isInstance(error)) {
			if (error.text) {
				const rescued = rescueFromText(error.text, schema);
				if (rescued) {
					logWarn("NoObjectGeneratedError rescued via schema defaults", {
						loggerScope: ["agents", "embedded"],
						extra: {
							errorMessage: error.message,
							finishReason: error.finishReason
						}
					});
					return {
						result: rescued,
						toolCalls: capturedToolCalls
					};
				}
			}
			logWarn("NoObjectGeneratedError could not be rescued", {
				loggerScope: ["agents", "embedded"],
				extra: {
					errorMessage: error.message,
					hasText: !!error.text,
					finishReason: error.finishReason
				}
			});
			throw new UserInputError("The AI was unable to process your query. Please try rephrasing.");
		}
		const providerError = resolveProviderApiCallError(error);
		if (providerError) throw toLLMProviderError(providerError, error);
		if (RetryError.isInstance(error)) throw new LLMProviderError(`The AI provider is currently unavailable: ${error.message}. Please try again later.`, { cause: error });
		if (error instanceof UserInputError || error instanceof ConfigurationError || error instanceof LLMProviderError || error instanceof AgentExecutionError) throw error;
		throw toAgentExecutionError(error);
	}
}
function toAgentExecutionError(error) {
	const eventId = logIssue(error, {
		loggerScope: ["agents", "embedded"],
		contexts: { embeddedAgent: {
			errorName: error instanceof Error ? error.name : typeof error,
			errorMessage: error instanceof Error ? error.message : String(error),
			isNoOutputGenerated: NoOutputGeneratedError.isInstance(error)
		} }
	});
	const detail = error instanceof Error && error.message ? error.message : "An unexpected error occurred while running the AI agent.";
	return new AgentExecutionError(`The AI agent failed to complete this request: ${detail}`, {
		cause: error,
		eventId
	});
}
function extractBalancedJsonObjects(text) {
	const objects = [];
	let start = -1;
	let depth = 0;
	let inString = false;
	let escaped = false;
	for (let i = 0; i < text.length; i++) {
		const ch = text[i];
		if (depth === 0) {
			if (ch === "{") {
				start = i;
				depth = 1;
				inString = false;
				escaped = false;
			}
			continue;
		}
		if (escaped) {
			escaped = false;
			continue;
		}
		if (ch === "\\" && inString) {
			escaped = true;
			continue;
		}
		if (ch === "\"") {
			inString = !inString;
			continue;
		}
		if (inString) continue;
		if (ch === "{") {
			depth++;
			continue;
		}
		if (ch === "}") {
			depth--;
			if (depth === 0 && start !== -1) {
				objects.push(text.slice(start, i + 1));
				start = -1;
				inString = false;
				escaped = false;
			}
		}
	}
	return objects;
}
/**
* Extract candidate JSON strings from text using multiple strategies.
* Handles plain JSON, markdown code blocks, and embedded JSON objects.
*/
function extractJsonCandidates(text) {
	const candidates = new Set([text]);
	const codeBlockRegex = /```(?:json)?\s*\n?([\s\S]*?)\n?```/g;
	const allMatches = Array.from(text.matchAll(codeBlockRegex));
	for (const match of allMatches) candidates.add(match[1].trim());
	for (const jsonObject of extractBalancedJsonObjects(text)) candidates.add(jsonObject);
	return Array.from(candidates);
}
/**
* Attempt to rescue a failed structured output by parsing raw LLM text through the schema.
* Schema defaults (e.g., `.default("")`) fill missing optional fields.
* Handles plain JSON, markdown code blocks, and JSON embedded in prose (e.g., Anthropic responses).
* Returns null if no candidate can be parsed or matched against the schema.
*/
function rescueFromText(text, schema) {
	for (const candidate of extractJsonCandidates(text)) try {
		const parsed = JSON.parse(candidate);
		const result = schema.safeParse(parsed);
		if (result.success) return result.data;
	} catch {}
	return null;
}

//#endregion
export { callEmbeddedAgent };
//# sourceMappingURL=callEmbeddedAgent-ffwEeqdP.js.map